import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { AlertTriangle, RefreshCw, ChevronDown, ChevronUp, History, PackageCheck } from "lucide-react";
import {
  eventHeadline,
  eventDetailLines,
  actorRailClass,
  actorLabel,
  groupByCorrelation,
  formatOccurredAt,
} from "@/lib/eventRendering";

/**
 * Phase 3A · Admin Activity Timeline.
 *
 * Reverse-chronological, correlation-grouped event list backed by
 * GET /api/admin/orders/{id}/activity. Shows a per-order "Activity log
 * incomplete" warning when outbox rows exist for THIS order.
 *
 * Phase 3C addition — delivery nudge banner rendered above the log when
 * the carrier has reported delivered but the order status has not yet
 * been flipped by an operator. Reuses the caller-provided
 * `onMarkDelivered` callback (which hits the existing status route).
 *
 * Presentation-only. All copy is derived client-side from structured
 * events; backend never sends render strings as source of truth.
 */
export default function OrderActivityTimeline({ orderId, orderStatus, onMarkDelivered }) {
  const [events, setEvents] = useState([]);
  const [outbox, setOutbox] = useState({ pending_count: 0, failure_count: 0, rows: [] });
  const [nextCursor, setNextCursor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState({});

  const load = async (cursor = null, append = false) => {
    if (!orderId) return;
    if (!append) setLoading(true);
    try {
      const qs = cursor ? `?cursor=${encodeURIComponent(cursor)}` : "";
      const r = await api.get(`/admin/orders/${orderId}/activity${qs}`);
      if (append) {
        setEvents((prev) => [...prev, ...(r.data.events || [])]);
      } else {
        setEvents(r.data.events || []);
      }
      setNextCursor(r.data.next_cursor || null);
      setOutbox(r.data.outbox || { pending_count: 0, failure_count: 0, rows: [] });
    } catch (e) {
      toast.error("Activity log unavailable");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  const retryOutbox = async (outboxId) => {
    setRetrying((s) => ({ ...s, [outboxId]: true }));
    try {
      await api.post(`/admin/event-outbox/${outboxId}/retry`);
      toast.success("Retry queued");
      setTimeout(() => load(), 1500);
    } catch (e) {
      toast.error("Retry failed");
    } finally {
      setRetrying((s) => ({ ...s, [outboxId]: false }));
    }
  };

  const groups = useMemo(() => groupByCorrelation(events), [events]);
  const hasLegacyOldest = events.length > 0 && events[events.length - 1]?.legacy_derived;
  const incompleteWarning = outbox.pending_count > 0 || outbox.failure_count > 0;

  // Phase 3C — delivery nudge. Show whenever carrier has reported delivered
  // AND the order status has not yet been flipped by an operator. No
  // dismiss: banner persists until the operator marks the order delivered.
  const deliveredEvent = useMemo(
    () => events.find((e) => e.event_type === "shipping.delivered") || null,
    [events],
  );
  const showDeliveryNudge = Boolean(deliveredEvent) && orderStatus && orderStatus !== "delivered";
  const [nudgeBusy, setNudgeBusy] = useState(false);
  const doMarkDelivered = async () => {
    if (typeof onMarkDelivered !== "function") return;
    setNudgeBusy(true);
    try {
      await onMarkDelivered();
    } finally {
      setNudgeBusy(false);
    }
  };

  return (
    <div data-testid="order-activity-timeline">
      {showDeliveryNudge && (
        <div
          className="mb-4 border border-amber-400 bg-amber-50 rounded-lg p-4"
          data-testid="delivery-nudge"
        >
          <div className="flex items-start gap-3">
            <PackageCheck className="h-5 w-5 text-amber-800 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="text-[13px] font-semibold text-amber-900" data-testid="delivery-nudge-headline">
                Carrier reports delivered
              </div>
              <div className="text-[12px] text-amber-900/80 mt-0.5" data-testid="delivery-nudge-body">
                {deliveredEvent?.metadata?.carrier
                  ? `${deliveredEvent.metadata.carrier} confirmed delivery`
                  : "The carrier confirmed delivery"}
                {deliveredEvent?.metadata?.occurred_at
                  ? ` on ${formatOccurredAt(deliveredEvent.metadata.occurred_at)}`
                  : ""}
                . Review and mark this order delivered.
              </div>
              <div className="mt-3">
                <button
                  type="button"
                  onClick={doMarkDelivered}
                  disabled={nudgeBusy}
                  className="px-4 py-1.5 border border-amber-800 bg-amber-100 rounded text-[12px] font-semibold uppercase tracking-widest text-amber-900 hover:bg-amber-200 disabled:opacity-50"
                  data-testid="delivery-nudge-mark-delivered"
                >
                  {nudgeBusy ? "Marking…" : "Mark delivered"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Per-order Activity-log incomplete warning */}
      {incompleteWarning && (
        <div
          className="mb-4 border border-amber-300 bg-amber-50 rounded-lg p-4"
          data-testid="activity-incomplete-warning"
        >
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-800 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="text-[13px] font-semibold text-amber-900">
                Activity log incomplete
              </div>
              <div className="text-[12px] text-amber-900/80 mt-0.5">
                {outbox.failure_count > 0 && (
                  <span data-testid="activity-incomplete-failure-count">
                    {outbox.failure_count} unrecovered event{outbox.failure_count === 1 ? "" : "s"}
                  </span>
                )}
                {outbox.failure_count > 0 && outbox.pending_count > 0 && " · "}
                {outbox.pending_count > 0 && (
                  <span data-testid="activity-incomplete-pending-count">
                    {outbox.pending_count} retrying
                  </span>
                )}
                {" — the timeline for this order may be missing recent activity."}
              </div>
              {outbox.rows.length > 0 && (
                <div className="mt-3 space-y-1.5">
                  {outbox.rows.map((r) => (
                    <div
                      key={r.outbox_id}
                      className="flex items-center gap-2 text-[11px] font-mono text-amber-900/85"
                      data-testid={`activity-outbox-row-${r.outbox_id}`}
                    >
                      <span className={`inline-block h-1.5 w-1.5 rounded-full ${r.state === "permanent_failure" ? "bg-red-600" : "bg-amber-500"}`} />
                      <span className="uppercase tracking-widest text-[10px]">{r.state}</span>
                      <span>·</span>
                      <span>{r.event_type}</span>
                      {r.attempts > 0 && <span className="opacity-70">· {r.attempts} attempts</span>}
                      {r.state === "permanent_failure" && (
                        <button
                          type="button"
                          onClick={() => retryOutbox(r.outbox_id)}
                          disabled={retrying[r.outbox_id]}
                          className="ml-2 px-2 py-0.5 border border-amber-800 rounded text-amber-900 hover:bg-amber-100"
                          data-testid={`activity-outbox-retry-${r.outbox_id}`}
                        >
                          {retrying[r.outbox_id] ? "Retrying…" : "Retry"}
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {loading && events.length === 0 && (
        <div className="text-charcoal/50 text-[13px] p-4" data-testid="activity-loading">
          Loading activity…
        </div>
      )}

      {!loading && events.length === 0 && (
        <div className="text-charcoal/50 text-[13px] p-4" data-testid="activity-empty">
          No stored activity for this order yet.
        </div>
      )}

      <div className="border border-borderSubtle bg-white rounded-lg divide-y divide-borderSubtle" data-testid="activity-feed">
        {groups.map((g, gi) => (
          <ActivityGroup key={`${g.correlation_id || "solo"}-${gi}`} group={g} groupIndex={gi} />
        ))}
      </div>

      {nextCursor && (
        <div className="mt-3 flex justify-center">
          <button
            type="button"
            onClick={() => load(nextCursor, true)}
            className="px-4 py-2 text-[12px] uppercase tracking-widest border border-borderSubtle rounded hover:bg-bone"
            data-testid="activity-load-more"
          >
            Load older
          </button>
        </div>
      )}

      {hasLegacyOldest && !nextCursor && events.length > 0 && (
        <div className="mt-3 text-[11px] text-charcoal/55 italic px-2" data-testid="activity-legacy-footer">
          Full activity log begins with Phase 3. Earlier events are reconstructed from partial history
          and may not include actor or before/after detail.
        </div>
      )}
    </div>
  );
}

function ActivityGroup({ group, groupIndex }) {
  const multi = group.events.length > 1;
  return (
    <div
      className={multi ? "border-l-2 border-oxblood/25 pl-3 py-1" : ""}
      data-testid={multi ? `activity-group-${groupIndex}` : undefined}
    >
      {group.events.map((ev, idx) => (
        <ActivityRow
          key={ev.event_id || `${groupIndex}-${idx}`}
          ev={ev}
          showTimestamp={idx === 0}
          testid={`activity-item-${groupIndex}-${idx}`}
        />
      ))}
    </div>
  );
}

function ActivityRow({ ev, showTimestamp, testid }) {
  const [expanded, setExpanded] = useState(false);
  const isRefundSummary = ev.event_type === "payment.refund_summary";
  const isLegacy = ev.legacy_derived;
  const detailLines = eventDetailLines(ev);
  const headline = eventHeadline(ev);
  const label = actorLabel(ev.actor);
  const rail = isRefundSummary ? "bg-[#DDA94E]" : actorRailClass(ev.actor?.type);
  const showToggle = (ev.metadata && Object.keys(ev.metadata).length > 0) || (ev.changes && ev.changes.length > 6);

  return (
    <div
      className={`px-4 py-3 flex items-start gap-3 ${isRefundSummary ? "bg-[#FFF8E7]/60" : ""}`}
      data-testid={testid}
    >
      <div className={`w-1 self-stretch rounded ${rail}`} aria-hidden="true" />
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2 mb-0.5">
          {showTimestamp && (
            <span
              className="text-[10px] uppercase tracking-[0.14em] font-mono text-charcoal/55"
              data-testid={`${testid}-time`}
            >
              {isRefundSummary && ev.metadata?.timestamp_unknown
                ? "Date unavailable"
                : formatOccurredAt(ev.occurred_at)}
            </span>
          )}
          {isLegacy && !isRefundSummary && (
            <span
              className="text-[9px] uppercase tracking-widest text-charcoal/45 border border-charcoal/15 rounded px-1 py-[1px] font-mono"
              data-testid={`${testid}-legacy-badge`}
            >
              Legacy
            </span>
          )}
          {isRefundSummary && (
            <span
              className="text-[9px] uppercase tracking-widest text-[#8A6A25] border border-[#DDA94E]/60 bg-[#FFF8E7] rounded px-1 py-[1px] font-mono"
              data-testid={`${testid}-refund-summary-badge`}
            >
              Historical
            </span>
          )}
        </div>
        <div className="text-[13px] text-charcoal font-medium" data-testid={`${testid}-headline`}>
          {headline}
        </div>
        {label && !isRefundSummary && (
          <div className="text-[11px] text-charcoal/60 mt-0.5" data-testid={`${testid}-actor`}>
            {label}
          </div>
        )}
        {isRefundSummary && (
          <div className="text-[11px] text-charcoal/60 mt-0.5" data-testid={`${testid}-actor`}>
            Legacy backfill · Individual transactions unavailable
          </div>
        )}
        {detailLines.length > 0 && (
          <div className="mt-1 space-y-0.5" data-testid={`${testid}-detail`}>
            {detailLines.map((line, i) => (
              <div key={i} className="text-[12px] text-charcoal/75 font-mono">
                {line}
              </div>
            ))}
          </div>
        )}
        {showToggle && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="mt-1 text-[10px] uppercase tracking-widest text-charcoal/60 hover:text-oxblood flex items-center gap-1"
            data-testid={`${testid}-toggle`}
          >
            {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            {expanded ? "Hide details" : "More details"}
          </button>
        )}
        {expanded && (
          <div className="mt-2 border border-borderSubtle rounded bg-bone/50 p-2 text-[11px] font-mono text-charcoal/80" data-testid={`${testid}-metadata`}>
            {Object.entries(ev.metadata || {}).map(([k, v]) => (
              <div key={k} className="flex gap-2">
                <span className="text-charcoal/50">{k}:</span>
                <span>{typeof v === "object" ? JSON.stringify(v) : String(v)}</span>
              </div>
            ))}
            {ev.changes && ev.changes.length > 0 && (
              <div className="mt-1 pt-1 border-t border-borderSubtle">
                <div className="text-charcoal/50 mb-0.5">changes:</div>
                {ev.changes.map((c, i) => (
                  <div key={i}>
                    {c.field}: {JSON.stringify(c.old)} → {JSON.stringify(c.new)}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
